type LogLevel = 'info' | 'warn' | 'error' | 'security' | 'audit';

interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  stack?: string;
  route?: string;
  component?: string;
}

interface BufferedLogEntry {
  id: string;
  timestamp: string;
  level: 'error' | 'warn';
  message: string;
  route?: string;
  component?: string;
  stack?: string;
  maskedContext?: string;
}

// In-memory buffer for error/warn logs (max 10 entries)
const LOG_BUFFER: BufferedLogEntry[] = [];
const MAX_BUFFER_SIZE = 10;

// PII masking patterns
const PII_PATTERNS = {
  // Swedish personal number (personnummer): YYYYMMDD-XXXX, YYYYMMDD+XXXX, or with century
  personalNumber: /\b\d{4}[-+]?\d{2}[-+]?\d{2}[-+]?\d{4}\b|\b\d{2}[-+]?\d{2}[-+]?\d{2}[-+]?\d{4}\b/g,
  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  // Swedish phone numbers: +46, 070, 072, 073, 076, 079, 08x, etc.
  phone: /\b(?:\+46|0[0-9]{1,3})[- ]?\d{3,8}\b/g,
  // Customer numbers (alphanumeric, 6-12 chars, often with prefix)
  customerNumber: /\b(?:KUND|K-|CUST-)[\w\d]{4,12}\b/gi,
};

// Fields that should be sanitized
const PII_FIELDS = new Set([
  'email',
  'phone',
  'address',
  'personalId',
  'personalNumber',
  'personnummer',
  'customerName',
  'userName',
  'password',
  'token',
  'internal_notes',
  'body',
  'description',
  'notes',
  'customerNumber',
  'kundnummer',
]);

/**
 * Mask PII in a string
 */
function maskString(str: string): string {
  if (typeof str !== 'string') return str;

  let masked = str;

  // Mask personal numbers
  masked = masked.replace(PII_PATTERNS.personalNumber, (match) => {
    const visible = match.slice(-4);
    return `****-****${visible}`;
  });

  // Mask emails
  masked = masked.replace(PII_PATTERNS.email, (match) => {
    const [local, domain] = match.split('@');
    if (!domain) return match;
    const maskedLocal = local.length > 2 ? `${local[0]}${local[1]}***` : '***';
    const domainParts = domain.split('.');
    const tld = domainParts.pop();
    const maskedDomain = domainParts.length > 0 ? `${domainParts.join('.')}.${tld}` : `***.${tld}`;
    return `${maskedLocal}@${maskedDomain}`;
  });

  // Mask phone numbers
  masked = masked.replace(PII_PATTERNS.phone, (match) => {
    const digits = match.replace(/\D/g, '');
    const visible = digits.slice(-3);
    return `***-***${visible}`;
  });

  // Mask customer numbers
  masked = masked.replace(PII_PATTERNS.customerNumber, (match) => {
    return `${match.slice(0, 4)}****`;
  });

  return masked;
}

/**
 * Recursively sanitize context object to remove PII
 */
function sanitizeContext(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return maskString(obj);
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeContext(item));
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Skip PII fields entirely
    if (PII_FIELDS.has(key) || key.includes('internal_notes') || key.includes('body')) {
      continue;
    }
    sanitized[key] = sanitizeContext(value);
  }
  return sanitized;
}

/**
 * Truncate string to max length
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...[truncated]';
}

/**
 * Get current route from window location
 */
function getCurrentRoute(): string {
  if (typeof window !== 'undefined') {
    return window.location.pathname + window.location.search;
  }
  return 'unknown';
}

/**
 * Generate unique ID for log entries
 */
function generateId(): string {
  return `log_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Add log entry to in-memory buffer (only for error/warn)
 */
function addToBuffer(entry: BufferedLogEntry): void {
  LOG_BUFFER.unshift(entry); // Add to beginning (newest first)
  if (LOG_BUFFER.length > MAX_BUFFER_SIZE) {
    LOG_BUFFER.pop(); // Remove oldest
  }
}

function formatLogEntry(entry: LogEntry): string {
  const isProduction = import.meta.env.PROD;
  if (isProduction) {
    return JSON.stringify(entry);
  }

  const levelColors: Record<LogLevel, string> = {
    info: '\x1b[32m',
    warn: '\x1b[33m',
    error: '\x1b[31m',
    security: '\x1b[35m',
    audit: '\x1b[36m',
  };
  const reset = '\x1b[0m';
  const color = levelColors[entry.level] || '';
  const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : '';
  const stackStr = entry.stack ? `\n  Stack: ${truncate(entry.stack, 500)}` : '';
  return `${color}[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}${contextStr}${stackStr}${reset}`;
}

function createLogger() {
  const log = (level: LogLevel, message: string, context?: Record<string, unknown>, stack?: string) => {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      message: maskString(message),
      context: context ? sanitizeContext(context) as Record<string, unknown> : undefined,
      stack: stack ? truncate(maskString(stack), 2000) : undefined,
      route: getCurrentRoute(),
    };

    // Add error/warn to buffer
    if (level === 'error' || level === 'warn') {
      const bufferedEntry: BufferedLogEntry = {
        id: entry.id,
        timestamp: entry.timestamp,
        level: level as 'error' | 'warn',
        message: entry.message,
        route: entry.route,
        component: (context?.component as string) || undefined,
        stack: entry.stack,
        maskedContext: entry.context ? JSON.stringify(entry.context).slice(0, 2000) : undefined,
      };
      addToBuffer(bufferedEntry);
    }

    const formatted = formatLogEntry(entry);

    switch (level) {
      case 'info':
        console.info(formatted);
        break;
      case 'warn':
        console.warn(formatted);
        break;
      case 'error':
        console.error(formatted);
        break;
      case 'security':
        console.warn(formatted);
        break;
      case 'audit':
        console.info(formatted);
        break;
    }
  };

  return {
    info: (message: string, context?: Record<string, unknown>) => log('info', message, context),
    warn: (message: string, context?: Record<string, unknown>) => log('warn', message, context),
    error: (message: string, context?: Record<string, unknown>, stack?: string) => log('error', message, context, stack),
    security: (message: string, context?: Record<string, unknown>) => log('security', message, context),
    audit: (message: string, context?: Record<string, unknown>) => log('audit', message, context),

    /**
     * Get recent error/warn logs for bug report
     */
    getRecentLogs: (): BufferedLogEntry[] => {
      return [...LOG_BUFFER]; // Return copy
    },

    /**
     * Clear the log buffer
     */
    clearBuffer: (): void => {
      LOG_BUFFER.length = 0;
    },

    /**
     * Mask a string for PII (useful for error messages)
     */
    maskPII: (str: string): string => {
      return maskString(str);
    },
  };
}

const logger = createLogger();
export default logger;
export type { BufferedLogEntry };
